import React, { useCallback, useEffect, useRef, useState } from 'react';
import { PlayButton } from '../play/PlayTarget';
import { AnimatePresence } from 'framer-motion';
import { toast } from 'react-toastify';
import { libraryDb } from '../library/libraryDb';
import { Entry } from '../library/entry';
import { useStorm } from '../storm/StormContext';
import GlitchText from '../chaos/GlitchText';
import { useFpsBackoff } from '../storm/useFpsBackoff';
import { pointerEngine, PlayGesture } from '../play/pointerEngine';
import ArchiveGrid from './ArchiveGrid';
import type { Tilt } from './PageField';
import BookOverlay from './BookOverlay';
import '../components/GuidedJournal.css';
import './Archive.css';

const PageField = React.lazy(() => import('./PageField'));

// Probed once per page load; the probe context is released immediately so it doesn't
// count against the browser's live WebGL context limit.
let webglSupport: boolean | null = null;
function hasWebGL(): boolean {
  if (webglSupport !== null) return webglSupport;
  try {
    const c = document.createElement('canvas');
    const gl = (c.getContext('webgl2') || c.getContext('webgl')) as WebGLRenderingContext | null;
    webglSupport = !!gl;
    gl?.getExtension('WEBGL_lose_context')?.loseContext();
  } catch { webglSupport = false; }
  return webglSupport;
}

// Device tilt (touch only): asked on the first touch tap (engine 'tap', so a swipe never spends
// the request) in the archive — iOS needs requestPermission inside a user gesture. A resolved
// 'denied' / no API → never asked again this session. A REJECTED request means "no user
// gesture", not denial: back to 'unasked' and mounted hooks re-arm for the next tap.
// 'pending' = prompt open: no second prompt; a grant still arrives (to a mounted hook only).
type TiltAccess = 'unasked' | 'pending' | 'granted' | 'denied';
let tiltAccess: TiltAccess = 'unasked';
const grantWaiters = new Set<() => void>(); // mounted hooks to start listening when a grant lands
const rearmWaiters = new Set<() => void>(); // mounted hooks to re-arm after a rejected request
const TILT_DEG = 30;

type OrientationCtor = { requestPermission?: () => Promise<string> };
function orientationApi(): OrientationCtor | null {
  return typeof DeviceOrientationEvent === 'undefined' ? null : (DeviceOrientationEvent as unknown as OrientationCtor);
}

function useDeviceTilt(enabled: boolean): React.MutableRefObject<Tilt> {
  const tilt = useRef<Tilt>({ x: 0, y: 0, on: false });
  useEffect(() => {
    const t = tilt.current;
    if (!enabled || tiltAccess === 'denied') return;
    let alive = true;
    let listening = false;
    let beta0: number | null = null;
    const clamp1 = (v: number) => Math.max(-1, Math.min(1, v / TILT_DEG));
    const onOrient = (e: DeviceOrientationEvent) => {
      if (e.gamma == null || e.beta == null) return;
      if (beta0 === null) beta0 = e.beta; // the way the phone is held now is neutral
      t.x = clamp1(e.gamma); t.y = clamp1(beta0 - e.beta); t.on = true;
    };
    const listen = () => {
      if (!alive || listening) return;
      listening = true;
      window.addEventListener('deviceorientation', onOrient);
    };
    let offTap: (() => void) | null = null;
    const disarm = () => { if (offTap) { offTap(); offTap = null; } };
    const onTap = (e: PlayGesture) => {
      if (e.type !== 'touch') return;
      disarm();
      if (tiltAccess !== 'unasked') return; // another hook already asked
      const api = orientationApi();
      if (!api) { tiltAccess = 'denied'; return; }
      if (typeof api.requestPermission !== 'function') { tiltAccess = 'granted'; listen(); return; }
      tiltAccess = 'pending';
      api.requestPermission()
        .then((r) => { tiltAccess = r === 'granted' ? 'granted' : 'denied'; if (tiltAccess === 'granted') grantWaiters.forEach((w) => w()); })
        .catch(() => { tiltAccess = 'unasked'; rearmWaiters.forEach((w) => w()); });
    };
    const arm = () => { if (alive && !offTap && tiltAccess === 'unasked') offTap = pointerEngine.on('tap', onTap); };
    grantWaiters.add(listen);
    rearmWaiters.add(arm);
    if (tiltAccess === 'granted') listen();
    else arm();
    return () => {
      alive = false;
      grantWaiters.delete(listen);
      rearmWaiters.delete(arm);
      disarm();
      window.removeEventListener('deviceorientation', onOrient);
      t.on = false; t.x = 0; t.y = 0;
    };
  }, [enabled]);
  return tilt;
}

class Guard extends React.Component<{ fallback: React.ReactNode; children: React.ReactNode }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch(err: unknown) { console.warn('[mania] archive WebGL failed, using grid', err); }
  render() { return this.state.failed ? this.props.fallback : this.props.children; }
}

const Archive: React.FC = () => {
  const { tear, setDepth, reducedMotion, surge } = useStorm();
  const [entries, setEntries] = useState<Entry[] | null>(null);
  const [open, setOpen] = useState<Entry | null>(null);
  const [isMobile] = useState(() => window.innerWidth <= 768);
  // Read ?new= once on mount (pure); the URL is cleared in a mount effect below.
  const [dropInId] = useState<string | null>(() => {
    try { return new URLSearchParams(window.location.search).get('new'); } catch { return null; }
  });
  useEffect(() => {
    try { if (dropInId) window.history.replaceState(null, '', '/journal'); } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [webglOk] = useState(hasWebGL);
  const webgl = !reducedMotion && webglOk;
  const q = useFpsBackoff();
  const tilt = useDeviceTilt(webgl && q < 2);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await libraryDb.migrateFromLocalStorage();
        const list = await libraryDb.list();
        if (!cancelled) setEntries(list);
        if (dropInId && !cancelled) void surge(0.6);
      } catch (e) {
        console.error('[mania] archive load failed', e);
        if (!cancelled) { setEntries([]); toast.error('Couldn’t open your library in this browser.'); }
      }
    })();
    return () => { cancelled = true; setDepth(0); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onEdit = useCallback((e: Entry) => tear({ to: `/write?edit=${encodeURIComponent(e.id)}` }), [tear]);
  const onDelete = useCallback(async (e: Entry) => {
    try {
      await libraryDb.remove(e.id);
      setOpen(null);
      setEntries((prev) => (prev ?? []).filter((x) => x.id !== e.id));
      tear({ strength: 0.5 });
    } catch (err) {
      console.error('[mania] delete failed', err);
      toast.error('Couldn’t delete that page.');
    }
  }, [tear]);

  if (entries === null) return <div className="archive-root" />;

  return (
    <div className="archive-root">
      {entries.length === 0 ? (
        <div className="archive-empty">
          <GlitchText as="h1" text="mania" tapBurst style={{ fontSize: 'clamp(64px, 14vw, 190px)', justifyContent: 'center' }} />
          <button className="archive-cta" onClick={() => tear({ to: '/write' })}>make your first page</button>
        </div>
      ) : webgl ? (
        <Guard fallback={<ArchiveGrid entries={entries} onOpen={setOpen} />}>
          <React.Suspense fallback={null}>
            <PageField entries={entries} isMobile={isMobile} dropInId={dropInId} paused={!!open}
              calm={reducedMotion} tilt={tilt}
              initialIndex={Math.max(0, entries.findIndex((x) => x.id === dropInId))}
              onOpen={(e) => { tear({ strength: 0.5 }); setOpen(e); }} onDepth={setDepth} />
          </React.Suspense>
        </Guard>
      ) : (
        <ArchiveGrid entries={entries} onOpen={setOpen} />
      )}
      {entries.length > 0 && (
        <PlayButton className="archive-new" onClick={() => tear({ to: '/write' })}>+ new page</PlayButton>
      )}
      <AnimatePresence>
        {open && <BookOverlay key={open.id} entry={open} reduce={reducedMotion} onClose={() => setOpen(null)} onEdit={onEdit} onDelete={onDelete} />}
      </AnimatePresence>
    </div>
  );
};

export default Archive;
